import { NumberField } from "@/components/ui/number-field";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, Clock, MapPin, Target, CheckSquare, Repeat, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { apiRequest } from "@/lib/queryClient";
import { formatDateInput } from "@/lib/dateUtils";
import type { Goal } from "@shared/schema";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { INTERNAL_CALENDARS_QUERY_KEY, type InternalCalendar } from "@/components/internal-calendars-panel";
import { isFutureGoal } from "@/lib/goal-visibility";

// Hand-written to avoid drizzle-zod + Zod 3.25 incompatibility (frontendCalendarEventSchema.extend() throws at module init)
const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  startDate: z.string(),
  startTime: z.string(),
  endDate: z.string(),
  endTime: z.string(),
  allDay: z.boolean().optional(),
  location: z.string().optional(),
  eventType: z.string().optional(),
  category: z.string().optional(),
  goalId: z.string().optional(),
  taskId: z.string().optional(),
  habitId: z.string().optional(),
  color: z.string().optional(),
  isRecurring: z.boolean().optional(),
  recurrenceRule: z.string().optional(),
  recurringEventId: z.string().optional(),
  externalCalendarId: z.string().optional(),
  externalEventId: z.string().optional(),
  isCompleted: z.boolean().optional(),
  repeatIfIncomplete: z.boolean().optional(),
  reminders: z.any().optional(),
  recurrenceFrequency: z.enum(["daily", "weekly", "bi-weekly", "monthly", "quarterly", "yearly"]).optional(),
  recurrenceCount: z.number().optional(),
  recurrenceEndDate: z.string().optional(),
  internalCalendarId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  goals: Goal[];
  selectedDate?: Date | null;
  defaultEventType?: string;
}

export default function CreateEventModal({ isOpen, onClose, goals, selectedDate, defaultEventType = "event" }: CreateEventModalProps) {
  const [isAllDay, setIsAllDay] = useState(false);
  const [isRecurringExpanded, setIsRecurringExpanded] = useState(false);
  const [recurrenceEndType, setRecurrenceEndType] = useState<"count" | "date">("count");
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch additional data for different event types
  const { data: tasks = [] } = useQuery<any[]>({
    queryKey: ["/api/tasks"],
    enabled: isOpen,
  });

  const { data: habits = [] } = useQuery<any[]>({
    queryKey: ["/api/habits"],
    enabled: isOpen,
  });

  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
    enabled: isOpen,
  });

  const { data: internalCalendars = [] } = useQuery<InternalCalendar[]>({
    queryKey: INTERNAL_CALENDARS_QUERY_KEY,
    enabled: isOpen,
  });

  // Fetch Google Calendar connections
  const { data: calendarConnections = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar/connections"],
    enabled: isOpen,
  });

  // Get Google calendars from connections
  const googleConnection = calendarConnections.find((c: any) => c.provider === 'google' && c.connected);
  const googleCalendars = googleConnection?.externalCalendars || [];

  // Auto-select primary calendar when modal opens
  useEffect(() => {
    if (isOpen && googleCalendars.length > 0 && selectedCalendarIds.length === 0) {
      const primaryCal = googleCalendars.find((c: any) => c.isPrimary);
      if (primaryCal) {
        setSelectedCalendarIds([primaryCal.id]);
      }
    }
  }, [isOpen, googleCalendars]);

  const toggleCalendarSelection = (calendarId: string) => {
    setSelectedCalendarIds(prev => 
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const formDate = selectedDate || new Date();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      eventType: defaultEventType,
      category: undefined,
      allDay: false,
      startDate: formatDateInput(formDate),
      startTime: "09:00",
      endDate: formatDateInput(formDate),
      endTime: "10:00",
      location: "",
      color: "#3b82f6",
      isRecurring: false,
      goalId: undefined,
      taskId: undefined,
      habitId: undefined,
      recurrenceRule: undefined,
      recurrenceFrequency: "weekly",
      recurrenceCount: 4,
      recurrenceEndDate: undefined,
    },
  });
  const selectedInternalCalendar = internalCalendars.find(
    calendar => calendar.id === form.watch("internalCalendarId"),
  );

  // Reset form when props change
  useEffect(() => {
    if (isOpen) {
      const formDate = selectedDate || new Date();
      form.reset({
        title: "",
        description: "",
        eventType: defaultEventType,
        category: undefined,
        allDay: false,
        startDate: formatDateInput(formDate),
        startTime: "09:00",
        endDate: formatDateInput(formDate),
        endTime: "10:00",
        location: "",
        color: "#3b82f6",
        internalCalendarId: undefined,
        isRecurring: false,
        goalId: undefined,
        taskId: undefined,
        habitId: undefined,
        recurrenceRule: undefined,
        recurrenceFrequency: "weekly",
        recurrenceCount: 4,
        recurrenceEndDate: undefined,
      });
      setIsAllDay(false);
      setIsRecurringExpanded(false);
      setRecurrenceEndType("count");
      setSelectedCalendarIds([]);
    }
  }, [isOpen, selectedDate, defaultEventType, form]);

  useEffect(() => {
    if (isOpen && internalCalendars.length > 0 && !form.getValues("internalCalendarId")) {
      form.setValue(
        "internalCalendarId",
        (internalCalendars.find(calendar => calendar.isDefault) || internalCalendars[0]).id,
      );
    }
  }, [isOpen, internalCalendars, form]);

  const createEventMutation = useMutation({
    mutationFn: (eventData: any) =>
      apiRequest("POST", "/api/calendar", eventData),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
      
      const eventCount = data?.count || (data?.events ? data.events.length : 1);
      const isRecurring = eventCount > 1;
      
      toast({
        title: isRecurring ? "Recurring Events Created" : "Event Created",
        description: isRecurring 
          ? `Successfully created ${eventCount} recurring events`
          : "Your calendar event has been created successfully",
      });
      
      onClose();
      form.reset();
    },
    onError: (error: any) => {
      console.error("Event creation error:", error);
      
      const errorMessage = error?.response?.data?.error 
        || error?.message 
        || "Failed to create event. Please try again.";
      
      toast({
        title: "Error Creating Event",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createEventMutation.isPending) submitLockRef.current = false;
  }, [createEventMutation.isPending]);

  const onSubmit = (data: FormData) => {
    if (submitLockRef.current || createEventMutation.isPending) return;
    submitLockRef.current = true;
    const startDateTime = isAllDay 
      ? new Date(`${data.startDate}T00:00:00`)
      : new Date(`${data.startDate}T${data.startTime}:00`);
    
    const endDateTime = isAllDay
      ? new Date(`${data.endDate}T23:59:59`)
      : new Date(`${data.endDate}T${data.endTime}:00`);

    const eventData: any = {
      title: data.title,
      description: data.description || undefined,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      allDay: isAllDay,
      location: data.location || undefined,
      eventType: data.eventType,
      category: data.category && data.category !== "none" && data.category !== "" ? data.category : undefined,
      goalId: data.goalId && data.goalId !== "" && data.goalId !== "none" ? data.goalId : undefined,
      taskId: data.taskId && data.taskId !== "" && data.taskId !== "none" ? data.taskId : undefined,
      habitId: data.habitId && data.habitId !== "" && data.habitId !== "none" ? data.habitId : undefined,
      color: data.color || "#3b82f6",
      isRecurring: data.isRecurring || false,
      recurrenceRule: data.recurrenceRule || undefined,
      internalCalendarId: data.internalCalendarId || undefined,
    };

    // Add recurrence data if recurring is enabled
    if (data.isRecurring && data.recurrenceFrequency) {
      eventData.recurrenceFrequency = data.recurrenceFrequency;
      if (recurrenceEndType === "count" && data.recurrenceCount) {
        eventData.recurrenceCount = data.recurrenceCount;
      } else if (recurrenceEndType === "date" && data.recurrenceEndDate) {
        eventData.recurrenceEndDate = data.recurrenceEndDate;
      }
    }

    // Add selected Google Calendar IDs for syncing
    if (selectedCalendarIds.length > 0 && !selectedInternalCalendar?.isPlanningOnly) {
      eventData.targetCalendarIds = selectedCalendarIds;
    }

    console.log("Creating event with data:", eventData);
    createEventMutation.mutate(eventData);
  };

  const eventTypeOptions = [
    { value: "event", label: "Event", icon: CalendarIcon },
    { value: "task", label: "Task", icon: CheckSquare },
    { value: "goal", label: "Goal Event", icon: Target },
    { value: "milestone", label: "Milestone", icon: Target },
    { value: "habit", label: "Habit Reminder", icon: Repeat },
    { value: "reminder", label: "Reminder", icon: Clock },
  ];

  const colorOptions = [
    { value: "#3b82f6", label: "Blue" },
    { value: "#ef4444", label: "Red" },
    { value: "#22c55e", label: "Green" },
    { value: "#f59e0b", label: "Yellow" },
    { value: "#8b5cf6", label: "Purple" },
    { value: "#06b6d4", label: "Cyan" },
    { value: "#ec4899", label: "Pink" },
    { value: "#6b7280", label: "Gray" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Create New {defaultEventType === "task" ? "Task" : 
                        defaultEventType === "habit" ? "Habit Reminder" :
                        defaultEventType === "milestone" ? "Milestone" : "Event"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Event title..." 
                      {...field} 
                      data-testid="input-event-title"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Event Type */}
            <FormField
              control={form.control}
              name="eventType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-event-type">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {eventTypeOptions.map(option => {
                        const IconComponent = option.icon;
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <IconComponent className="h-4 w-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category Selection */}
            {internalCalendars.length > 0 && (
              <FormField
                control={form.control}
                name="internalCalendarId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Calendar</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-internal-calendar">
                          <SelectValue placeholder="Select a calendar" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {internalCalendars.map(calendar => (
                          <SelectItem key={calendar.id} value={calendar.id}>
                            <span className="flex items-center gap-2">
                              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: calendar.color || "#3b82f6" }} />
                              {calendar.name}{calendar.isPlanningOnly ? " (Planning only)" : ""}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedInternalCalendar?.isPlanningOnly && (
                      <p className="text-xs text-muted-foreground" data-testid="text-planning-calendar-notice">
                        Planning only — this event stays in GoalVia and will not sync to connected calendars.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Category Selection */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger data-testid="select-event-category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      <CategorySelectGroups categories={categories} />
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Goal Selection (if goal event type) */}
            {form.watch("eventType") === "goal" && (
              <FormField
                control={form.control}
                name="goalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Goal</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a goal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {goals.filter(goal => !isFutureGoal(goal)).map(goal => (
                          <SelectItem key={goal.id} value={goal.id}>
                            {goal.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Task Selection (if task event type) */}
            {form.watch("eventType") === "task" && (
              <FormField
                control={form.control}
                name="taskId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Task (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an existing task or leave blank to create new" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {tasks.map((task: any) => (
                          <SelectItem key={task.id} value={task.id}>
                            {task.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Habit Selection (if habit reminder type) */}
            {form.watch("eventType") === "habit" && (
              <FormField
                control={form.control}
                name="habitId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Habit</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a habit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {habits.map((habit: any) => (
                          <SelectItem key={habit.id} value={habit.id}>
                            {habit.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Event description..." 
                      className="resize-none"
                      rows={3}
                      {...field}
                      value={field.value || ""}
                      data-testid="textarea-event-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* All Day Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="allDay"
                checked={isAllDay}
                onCheckedChange={(checked) => setIsAllDay(checked === true)}
                data-testid="checkbox-all-day"
              />
              <label htmlFor="allDay" className="text-sm font-medium">
                All day event
              </label>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        data-testid="input-start-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        data-testid="input-end-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!isAllDay && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Time</FormLabel>
                      <FormControl>
                        <Input 
                          type="time" 
                          {...field} 
                          data-testid="input-start-time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Time</FormLabel>
                      <FormControl>
                        <Input 
                          type="time" 
                          {...field} 
                          data-testid="input-end-time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Event location..." 
                        className="pl-9"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-event-location"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Color */}
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {colorOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: option.value }}
                            />
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Google Calendar Selector - only show if connected */}
            {googleCalendars.length > 0 && !selectedInternalCalendar?.isPlanningOnly && (
              <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  <span className="font-medium">Sync to Google Calendar</span>
                  <Badge variant="outline" className="text-xs">
                    {selectedCalendarIds.length} selected
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Select which Google Calendar(s) this event should appear on:
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {googleCalendars.map((cal: any) => (
                    <div 
                      key={cal.id} 
                      className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer"
                      onClick={() => toggleCalendarSelection(cal.id)}
                      data-testid={`checkbox-calendar-${cal.id}`}
                    >
                      <Checkbox
                        checked={selectedCalendarIds.includes(cal.id)}
                        onCheckedChange={() => toggleCalendarSelection(cal.id)}
                      />
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cal.color || '#3b82f6' }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm truncate block">{cal.name}</span>
                        {cal.isPrimary && (
                          <Badge variant="secondary" className="text-xs mt-1">Primary</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedCalendarIds.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    No calendars selected - event will only be saved locally
                  </p>
                )}
              </div>
            )}

            {/* Recurring Section - Only for Tasks */}
            {form.watch("eventType") === "task" && (
              <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4" />
                    <span className="font-medium">Recurring Task</span>
                  </div>
                  <Button
                    type="button"
                    variant={form.watch("isRecurring") ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const current = form.getValues("isRecurring");
                      form.setValue("isRecurring", !current);
                      setIsRecurringExpanded(!current);
                    }}
                    data-testid="button-toggle-calendar-recurring"
                  >
                    {form.watch("isRecurring") ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Enabled
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Enable Recurring
                      </>
                    )}
                  </Button>
                </div>

                {form.watch("isRecurring") && isRecurringExpanded && (
                  <div className="space-y-3 pt-2">
                    {/* Frequency Selector */}
                    <FormField
                      control={form.control}
                      name="recurrenceFrequency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Frequency</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || "weekly"}>
                            <FormControl>
                              <SelectTrigger data-testid="select-calendar-recurrence-frequency">
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="yearly">Yearly</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* End Condition */}
                    <div className="space-y-2">
                      <FormLabel>End Condition</FormLabel>
                      <RadioGroup
                        value={recurrenceEndType}
                        onValueChange={(value: "count" | "date") => setRecurrenceEndType(value)}
                        className="space-y-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="count" id="end-count" data-testid="radio-calendar-end-count" />
                          <label htmlFor="end-count" className="text-sm flex-1">
                            Number of occurrences
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="date" id="end-date" data-testid="radio-calendar-end-date" />
                          <label htmlFor="end-date" className="text-sm flex-1">
                            End date
                          </label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Occurrence Count Input */}
                    {recurrenceEndType === "count" && (
                      <FormField
                        control={form.control}
                        name="recurrenceCount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Number of Occurrences</FormLabel>
                            <FormControl>
                              <NumberField
                                min={1}
                                max={100}
                                emptyValue={1}
                                value={field.value}
                                onValueChange={(v) => field.onChange(v ?? 1)}
                                name={field.name}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                data-testid="input-calendar-recurrence-count"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* End Date Input */}
                    {recurrenceEndType === "date" && (
                      <FormField
                        control={form.control}
                        name="recurrenceEndDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>End Date</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-calendar-recurrence-end-date"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Recurrence Summary */}
                    {form.watch("recurrenceFrequency") && (
                      <div className="text-sm text-muted-foreground bg-background p-3 rounded-md border">
                        <p className="font-medium mb-1">Recurrence Summary:</p>
                        <p>
                          Creating <strong>{form.watch("recurrenceFrequency")}</strong> tasks at{" "}
                          <strong>{form.watch("startTime") || "09:00"}</strong>
                          {recurrenceEndType === "count" && form.watch("recurrenceCount") && (
                            <> for <strong>{form.watch("recurrenceCount")}</strong> occurrences</>
                          )}
                          {recurrenceEndType === "date" && form.watch("recurrenceEndDate") && (
                            <> until <strong>{new Date(form.watch("recurrenceEndDate")!).toLocaleDateString()}</strong></>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                data-testid="button-cancel-event"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitLockRef.current || createEventMutation.isPending}
                data-testid="button-save-event"
              >
                {createEventMutation.isPending ? "Creating..." : "Create Event"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}